import React, { useState, useEffect, useRef } from "react";
import { useZxing } from "react-zxing";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { api } from "../../../utils/api";
import { redemptionOtpChannelsMessage } from "../../../utils/redemptionOtpChannelsMessage";
import { redemptionGet, redemptionPost } from "../../../utils/redemptionScanApi";
import {
  buildSubscriptionPickerRows,
  formatExpiryLabel,
  formatSubscriptionIdSuffix,
  pickEligibleSubscriptions,
} from "../../../utils/redemptionSubscriptionPicker";
import {
  OTP_DELIVERY,
  otpStepStatusMessage,
} from "../../../utils/redemptionOtpStepStatus";
import RedemptionSubscriptionPicker from "./RedemptionSubscriptionPicker";
import {
  classifyScannerInput,
  COFFEE_PASS_EXPIRED_QR_MESSAGE,
  COFFEE_PASS_LIVE_QR_MESSAGE,
  isCoffeePassSubscription,
} from "../../../utils/coffeePass";
import GlobalLoadingSpinner from "../../../components/GlobalLoadingSpinner";
import VehicleImageUpload from "./VehicleImageUpload";
import Compressor from "compressorjs";
import ModalPopup from "../../../components/ModalPopup";

const RedemptionScanner = ({ callback, closeModal }) => {
  const [scanResult, setScanResult] = useState("");
  const [isLoadingVideo, setIsLoadingVideo] = useState(true);
  const [subscriptionIdInput, setSubscriptionIdInput] = useState("");
  const [regoInput, setRegoInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState("qrcode");
  const [error, setError] = useState("");
  const [errorDetails, setErrorDetails] = useState("");
  const [success, setSuccess] = useState("");
  const [vehicleImage, setVehicleImage] = useState(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(""); // Visible status for mobile debugging
  const [redemptionData, setRedemptionData] = useState({
    vehicle: null,
    product: null,
    subscriptionId: null
  });
  // Contract validation popup for merchants
  const [contractValidationModalOpen, setContractValidationModalOpen] = useState(false);
  const [contractValidationMessage, setContractValidationMessage] = useState("");
  // OTP verification
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOTP, setSendingOTP] = useState(false);
  const [validatingOTP, setValidatingOTP] = useState(false);
  // Manual approval for rego verification
  const [manualApprovalData, setManualApprovalData] = useState(null);
  const [approvingRedemption, setApprovingRedemption] = useState(false);
  // Subscription confirmation details
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [loadingSubscriptionDetails, setLoadingSubscriptionDetails] = useState(false);
  // Vehicle rego from subscription (for step 4 display)
  const [vehicleRego, setVehicleRego] = useState(null);
  // Multiple active subscriptions found for a rego - merchant picks one
  const [subscriptionOptions, setSubscriptionOptions] = useState(null);
  const [subscriptionPickerRows, setSubscriptionPickerRows] = useState(null);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [otpDeliveryStatus, setOtpDeliveryStatus] = useState(OTP_DELIVERY.IDLE);
  const [otpChannels, setOtpChannels] = useState(null);
  const [loadedSubscriptionSummary, setLoadedSubscriptionSummary] = useState(null);
  // Store subscriptionId in a ref to ensure it persists through async state updates
  const subscriptionIdRef = useRef(null);
  const otpAutoSendAttemptedRef = useRef(false);
  // Live member QR: "cafe" (no OTP, no photo) or "wash-live" (no OTP, photo stays). null otherwise.
  const [liveQrMode, setLiveQrMode] = useState(null);
  // Ticket from resolve-redeem-qr, sent with the redemption so the server can check it
  const redemptionTicketRef = useRef(null);
  const [cafeRedeeming, setCafeRedeeming] = useState(false);

  const resetLiveQr = () => {
    setLiveQrMode(null);
    redemptionTicketRef.current = null;
  };

  const revealManualEntry = () => setShowManualEntry(true);

  const scannerPaused = isSubmitting || step !== "qrcode";

  const { ref } = useZxing({
    paused: scannerPaused,
    onDecodeResult(scanResult) {
      setScanResult(scanResult.getText());
    },
    onDecodeError() {
      // Continuous decode attempts — no UI noise
    },
  });

  const handleVideoPlaying = () => setIsLoadingVideo(false);

  const checkContractAcceptance = async (subscriptionId, productType) => {
    try {
      console.log("🔍 Frontend: Checking contract acceptance for:", { subscriptionId, productType });
      console.log("🔍 Frontend: Product type check - typeof:", typeof productType, "value:", productType);

      const contractPayload = await redemptionGet(
        `v1/contract/subscription/${subscriptionId}`
      );
      console.log("📡 Frontend: Contract API response:", contractPayload);

      if (contractPayload.success) {
        const contractData = contractPayload.data.contract;
        const requiresContract = contractPayload.data.requiresContract;
        const isSigned = contractPayload.data.isSigned;

        // If contract is null/undefined, it means no contract exists at all
        const contractExists = contractData !== null && contractData !== undefined;
        const contractStatus = contractData?.status || (contractExists ? "pending" : "not_found");

        console.log("✅ Frontend: Contract check result:", {
          requiresContract,
          isSigned,
          contractExists,
          contractStatus
        });

        // Always return the full contract check result
        // The caller will decide whether to show popup based on requiresContract, isSigned, and contractExists
        const result = {
          requiresContract: requiresContract,
          isSigned: isSigned,
          contractStatus: contractStatus,
          contractExists: contractExists
        };

        console.log("✅ Frontend: Contract check returning:", result);
        return result;
      } else {
        console.log("⚠️ Frontend: Contract API failed, assuming contract required but not signed");
        // If API fails, assume contract is required but not signed
        return {
          requiresContract: true,
          isSigned: false,
          contractStatus: "unknown",
          contractExists: false
        };
      }
    } catch (error) {
      console.error("❌ Frontend: Error checking contract acceptance:", error);
      // If check fails, assume contract is required but not signed
      return {
        requiresContract: true,
        isSigned: false,
        contractStatus: "error",
        contractExists: false
      };
    }
  };

  // Processes a resolved subscription object (from ID lookup, rego lookup, or the multi-subscription picker).
  // skipOtp: a live member QR already proves the member is present (wash with the rotating QR on).
  const processSubscription = async (subscription, { skipOtp = false } = {}) => {
        const { active, vehicle, product, _id: subscriptionId } = subscription;

        console.log("🔍 Subscription response - Full subscription object:", subscription);
        console.log("🔍 Subscription response - Vehicle object:", {
          vehicle: vehicle,
          vehicleId: vehicle?._id,
          vehicleRego: vehicle?.rego,
          vehicleType: typeof vehicle,
          vehicleKeys: vehicle ? Object.keys(vehicle) : "vehicle is null/undefined",
          vehicleIsPopulated: vehicle && typeof vehicle === "object" && vehicle._id ? "YES" : "NO"
        });
        console.log("🔍 Subscription response - Product object:", {
          product: product,
          productId: product?._id,
          productName: product?.name,
          productType: typeof product,
          productKeys: product ? Object.keys(product) : "product is null/undefined",
          productIsPopulated: product && typeof product === "object" && product._id ? "YES" : "NO"
        });

        if (!active) {
          setError("Subscription Not Active");
          setErrorDetails(
            "This subscription is currently inactive. The customer's subscription may have been deactivated or expired. Please verify the subscription status with the customer."
          );
          setIsSubmitting(false);
          return;
        }

        // Helper function to ensure product is populated (has _id)
        const ensureProductIsPopulated = async (productData) => {
          // If product is already an object with _id, return it
          if (productData && typeof productData === "object" && productData._id) {
            console.log("✅ Product is already populated:", productData._id);
            return productData;
          }

          // If product is just an ID string, fetch the full product object
          if (productData && typeof productData === "string") {
            console.log("⚠️ Product is just an ID, fetching product details...");
            try {
              const productPayload = await redemptionGet(`v1/product/${productData}`);
              if (productPayload.data?.product) {
                console.log("✅ Fetched product details:", productPayload.data.product._id);
                return productPayload.data.product;
              }
            } catch (productError) {
              console.error("❌ Error fetching product:", productError);
              throw productError;
            }
          }

          // If product is null/undefined, log warning and return null
          if (!productData) {
            console.warn("⚠️ Product is null or undefined in subscription");
          }

          return productData;
        };

        // Ensure product is populated BEFORE using it (needed for contract check and later steps)
        const populatedProduct = await ensureProductIsPopulated(product);

        // Validate that we have a valid product EARLY (before contract check)
        if (!populatedProduct || !populatedProduct._id) {
          setError("Product Data Missing");
          setErrorDetails(
            "Unable to retrieve product information from the subscription. The subscription may be invalid or the product may have been deleted. Please try scanning the QR code again or contact support."
          );
          setIsSubmitting(false);
          setContractValidationModalOpen(false);
          return;
        }

        // Check if contract acceptance is required and signed - now we can safely use populatedProduct.type
        const contractCheck = await checkContractAcceptance(subscriptionId, populatedProduct.type);
        console.log("🔍 Frontend: Contract check result in handleSubmit:", contractCheck);
        console.log("🔍 Frontend: Product type:", populatedProduct.type);
        console.log("🔍 Frontend: Product object:", populatedProduct);

        // Show popup if contract is required but not signed OR if no contract exists
        // Handle undefined contractExists as false (no contract found)
        const contractExists = contractCheck.contractExists !== undefined ? contractCheck.contractExists : false;
        const shouldShowPopup = contractCheck.requiresContract && (!contractCheck.isSigned || !contractExists);

        console.log("🔍 Frontend: Should show popup?", {
          requiresContract: contractCheck.requiresContract,
          isSigned: contractCheck.isSigned,
          contractExists: contractCheck.contractExists,
          contractExistsNormalized: contractExists,
          shouldShowPopup: shouldShowPopup
        });

        if (shouldShowPopup) {
          console.log("🚨 Frontend: Contract required but not signed or not found - showing popup");

          // Build appropriate message based on contract status
          let message = "";
          if (!contractExists) {
            message =
              `No contract found for this subscription. ` +
              `The customer needs to sign their subscription contract before redeeming. ` +
              `Please ask the customer to log into their customer portal and sign the contract.`;
          } else {
            message =
              `Customer needs to sign their subscription contract before redeeming. ` +
              `Please ask the customer to log into their customer portal and sign the contract. ` +
              `Contract Status: ${contractCheck.contractStatus || "pending"}`;
          }

          console.log("📝 Frontend: Setting contract validation message:", message);
          setContractValidationMessage(message);
          console.log("🚨 Frontend: Opening contract validation modal");
          setContractValidationModalOpen(true);
          setIsSubmitting(false);
          return;
        } else {
          console.log("✅ Frontend: Contract check passed, proceeding with redemption");
          console.log(
            "✅ Frontend: Contract details - requiresContract:",
            contractCheck.requiresContract,
            "isSigned:",
            contractCheck.isSigned,
            "contractExists:",
            contractExists
          );
        }

        // Contract is signed or not required, proceed with redemption
        console.log("🔍 [AFTER CONTRACT CHECK] Setting redemptionData with vehicle:", {
          vehicleId: vehicle?._id,
          vehicleRego: vehicle?.rego,
          vehicleObject: vehicle,
          vehicleType: typeof vehicle,
          vehicleKeys: vehicle ? Object.keys(vehicle) : "vehicle is null/undefined",
          vehicleIsObject: typeof vehicle === "object",
          vehicleIsString: typeof vehicle === "string",
          vehicleIsNull: vehicle === null,
          vehicleIsUndefined: vehicle === undefined
        });
        console.log("🔍 Subscription response - Product object:", {
          product: product,
          productId: product?._id,
          productName: product?.name,
          productType: typeof product,
          productKeys: product ? Object.keys(product) : "product is null/undefined",
          productIsPopulated: product && typeof product === "object" && product._id ? "YES" : "NO"
        });
        console.log("🔍 Using populatedProduct (from earlier check):", {
          populatedProduct: populatedProduct,
          populatedProductId: populatedProduct?._id,
          populatedProductName: populatedProduct?.name
        });

        // Store vehicle rego separately for step 4 display
        // Handle both cases: vehicle as object (populated) or vehicle as ID (needs to be populated)
        if (vehicle) {
          if (typeof vehicle === "object" && vehicle.rego) {
            console.log("✅ Vehicle is populated object, storing rego:", vehicle.rego);
            setVehicleRego(vehicle.rego);
          } else if (typeof vehicle === "string") {
            console.log("⚠️ Vehicle is just an ID, not populated. Need to fetch vehicle details.");
            // Vehicle is just an ID, we need to fetch it
            try {
              const vehiclePayload = await redemptionGet(`v1/vehicle/${vehicle}`);
              if (vehiclePayload.data?.vehicle?.rego) {
                console.log("✅ Fetched vehicle details, storing rego:", vehiclePayload.data.vehicle.rego);
                setVehicleRego(vehiclePayload.data.vehicle.rego);
                // Update vehicle in redemptionData with full object (preserve existing data)
                setRedemptionData((prev) => ({
                  ...prev,
                  vehicle: vehiclePayload.data.vehicle,
                  product: populatedProduct, // Use populated product
                  subscriptionId: subscriptionId
                }));
              } else {
                setRedemptionData((prev) => ({
                  ...prev,
                  vehicle,
                  product: populatedProduct, // Use populated product
                  subscriptionId: subscriptionId
                }));
              }
            } catch (vehicleError) {
              console.error("❌ Error fetching vehicle:", vehicleError);
              setRedemptionData((prev) => ({
                ...prev,
                vehicle,
                product: populatedProduct, // Use populated product
                subscriptionId: subscriptionId
              }));
            }
          } else {
            setRedemptionData((prev) => ({
              ...prev,
              vehicle,
              product: populatedProduct, // Use populated product
              subscriptionId: subscriptionId
            }));
          }
        } else {
          setRedemptionData((prev) => ({
            ...prev,
            vehicle,
            product: populatedProduct, // Use populated product
            subscriptionId: subscriptionId
          }));
        }

        // Store subscriptionId in ref for persistent access (avoids React async state issues)
        subscriptionIdRef.current = subscriptionId;

        const regoLabel =
          (typeof vehicle === "object" && vehicle?.rego) || vehicleRego || "—";
        setLoadedSubscriptionSummary({
          productName: populatedProduct.name,
          rego: regoLabel,
          idSuffix: formatSubscriptionIdSuffix(subscriptionId),
          expiryLabel: formatExpiryLabel(subscription.validUntilDateUTC),
        });
        setOtpDeliveryStatus(OTP_DELIVERY.IDLE);
        setOtpChannels(null);
        setOtpSent(false);
        setSuccess("");
        setError("");
        setErrorDetails("");
        otpAutoSendAttemptedRef.current = false;
        if (skipOtp) {
          setSubscriptionDetails({
            subscriptionId,
            product: populatedProduct,
            vehicle,
            customer: typeof vehicle?.customer === "object" ? vehicle.customer : null,
            active: subscription.active,
            validUntilDateUTC: subscription.validUntilDateUTC,
            startDateUTC: subscription.startDateUTC
          });
          setStep("confirmation");
          return;
        }
        setStep("otp-verification");
  };

  const showLiveQrError = (response) => {
    const code = response?.data?.code || response?.data?.errorCode;
    if (code === "REDEEM_QR_EXPIRED") {
      setError("Code Expired");
      setErrorDetails(COFFEE_PASS_EXPIRED_QR_MESSAGE);
    } else if (code === "REDEEM_QR_INVALID") {
      setError("Code Not Recognised");
      setErrorDetails(response?.data?.message || COFFEE_PASS_LIVE_QR_MESSAGE);
    } else {
      setError(response?.data?.message || "Could Not Read Membership");
      setErrorDetails(response?.data?.details || "Ask the member to show the live QR again and rescan.");
    }
  };

  // Live member QR (P1R.*): the server checks the signature and expiry and returns a short ticket.
  const handleLiveQr = async (payload) => {
    const merchantStore = sessionStorage.getItem("selectedMerchantStore");
    if (!merchantStore) {
      setError("Store Not Selected");
      setErrorDetails("Select the store you are redeeming at, then scan the live QR again.");
      return;
    }
    const response = await api("v1/subscription/resolve-redeem-qr", "POST", { payload, merchantStore });
    if (!response?.data?.success) {
      showLiveQrError(response);
      return;
    }
    const { subscription, redemptionTicket } = response.data.data;
    redemptionTicketRef.current = redemptionTicket;

    if (isCoffeePassSubscription(subscription)) {
      setLiveQrMode("cafe");
      subscriptionIdRef.current = subscription._id;
      setRedemptionData({
        vehicle: subscription.vehicle,
        product: subscription.product,
        subscriptionId: subscription._id
      });
      setVehicleRego(null);
      setSubscriptionDetails({
        subscriptionId: subscription._id,
        product: subscription.product,
        vehicle: subscription.vehicle,
        memberName: subscription.memberName,
        active: subscription.active,
        validUntilDateUTC: subscription.validUntilDateUTC,
        redemption: subscription.redemption
      });
      setSuccess("");
      setStep("confirmation");
      return;
    }

    setLiveQrMode("wash-live");
    await processSubscription(subscription, { skipOtp: true });
  };

  // Coffee Pass: no OTP, no photo. The ticket from the live QR is the proof.
  const handleCafeRedeem = async () => {
    if (cafeRedeeming) return;
    const subscriptionId = subscriptionIdRef.current || redemptionData?.subscriptionId;
    if (!subscriptionId || !redemptionData?.vehicle?._id || !redemptionData?.product?._id) {
      setError("Membership Not Loaded");
      setErrorDetails(COFFEE_PASS_LIVE_QR_MESSAGE);
      return;
    }
    setCafeRedeeming(true);
    setError("");
    setErrorDetails("");
    try {
      const currentUser = JSON.parse(sessionStorage.getItem("currentUser"));
      const response = await api("v1/redemption", "POST", {
        vehicle: redemptionData.vehicle._id,
        product: redemptionData.product._id,
        subscriptionId,
        merchantStore: sessionStorage.getItem("selectedMerchantStore"),
        merchantUser: currentUser?._id,
        redemptionDateUTC: new Date(),
        active: true,
        idempotencyKey: `${Date.now()}-${Math.random().toString(36).slice(2, 11)}-${subscriptionId}`,
        selectedPortal: sessionStorage.getItem("selectedPortal"),
        redemptionTicket: redemptionTicketRef.current
      });
      if (response?.data?.success) {
        setSuccess(response.data.data?.isDuplicate ? "Already Recorded" : "Coffee Redeemed");
        setTimeout(() => {
          closeModal();
          callback();
        }, 2000);
      } else {
        setError(response?.data?.message || "Redemption Failed");
        setErrorDetails(
          response?.data?.errorCode === "REDEEM_TICKET_INVALID"
            ? "This scan is no longer valid. Ask the member to show the live QR again and rescan."
            : response?.data?.details || "The coffee could not be recorded. Please try again."
        );
      }
    } catch (err) {
      setError("Network Error");
      setErrorDetails("Could not reach the server. Check the connection and try again.");
    } finally {
      setCafeRedeeming(false);
    }
  };

  const handleSubmit = async (result) => {
    const input = (result || "").trim();
    if (!input) return;

    if (isSubmitting) {
      return;
    }

    setError("");
    setErrorDetails("");
    setSubscriptionOptions(null);
    setSubscriptionPickerRows(null);
    setIsSubmitting(true);

    // Close any existing contract validation modal
    setContractValidationModalOpen(false);

    try {
      // Live member QR first, then the 24-char Mongo ObjectId from static QR codes and the
      // grey hash under them; anything else typed in the field is treated as a vehicle rego
      const route = classifyScannerInput(input);

      if (route.kind === "rotating") {
        await handleLiveQr(route.value);
      } else if (route.kind === "subscriptionId") {
        resetLiveQr();
        const payload = await redemptionGet(`v1/subscription/${input}`);
        const subscription = payload.data.subscription;
        if (isCoffeePassSubscription(subscription)) {
          // Coffee Pass has no typed-id fallback; only the live QR redeems it.
          setError("Live QR Required");
          setErrorDetails(COFFEE_PASS_LIVE_QR_MESSAGE);
        } else {
          await processSubscription(subscription);
        }
      } else {
        resetLiveQr();
        let payload;
        try {
          payload = await redemptionGet(
            `v1/subscription/by-rego/${encodeURIComponent(input)}`
          );
        } catch (regoError) {
          const msg = regoError.message || "";
          if (/not found|404/i.test(msg)) {
            revealManualEntry();
            setError("Subscription Not Found");
            setErrorDetails(
              `No subscription found for rego "${input.toUpperCase()}". Enter the subscription ID from the customer's app, or check the rego and try again.`
            );
            setIsSubmitting(false);
            return;
          }
          throw regoError;
        }

        // Coffee passes share the placeholder rego and are never offered from a rego search.
        const subscriptions = (payload.data?.subscriptions || []).filter(
          (s) => !isCoffeePassSubscription(s)
        );
        const rows = buildSubscriptionPickerRows(subscriptions);
        setSubscriptionPickerRows(rows);

        const eligibleRows = pickEligibleSubscriptions(subscriptions);
        if (subscriptions.length === 0) {
          revealManualEntry();
          setError("Subscription Not Found");
          setErrorDetails(`No subscriptions found for rego "${input.toUpperCase()}".`);
        } else if (eligibleRows.length === 0) {
          revealManualEntry();
          setError("No Redeemable Subscriptions");
          setErrorDetails(
            `Subscriptions exist for rego "${input.toUpperCase()}" but none can be redeemed right now. See reasons below and ask the customer to resolve (e.g. sign contract) or contact support.`
          );
          setSubscriptionOptions(subscriptions);
        } else if (eligibleRows.length === 1) {
          const chosen = subscriptions.find(
            (s) => String(s._id) === eligibleRows[0].id
          );
          await processSubscription(chosen);
        } else {
          setSubscriptionOptions(subscriptions);
        }
      }
    } catch (error) {
      console.error("Error processing subscription:", error);
      if (classifyScannerInput(input).kind === "rego") {
        revealManualEntry();
      }
      setError("Error Processing Request");
      setErrorDetails(
        error.response?.data?.details ||
          error.response?.data?.message ||
          error.message ||
          `An unexpected error occurred. Please try again or contact support if the problem persists.`
      );
      // Close contract validation modal on error
      setContractValidationModalOpen(false);
    }

    setIsSubmitting(false);
  };

  const handleSelectSubscriptionOption = async (subscription) => {
    if (isSubmitting) return;
    const row = buildSubscriptionPickerRows([subscription])[0];
    if (row && !row.selectable) {
      revealManualEntry();
      setError("Not Redeemable");
      setErrorDetails(row.blockedReason || "This subscription cannot be redeemed.");
      return;
    }

    setSubscriptionOptions(null);
    setSubscriptionPickerRows(null);
    setError("");
    setErrorDetails("");
    setIsSubmitting(true);

    try {
      await processSubscription(subscription);
    } catch (error) {
      console.error("Error processing subscription:", error);
      revealManualEntry();
      setError("Error Processing Request");
      setErrorDetails(
        error.response?.data?.details ||
          error.response?.data?.message ||
          `An unexpected error occurred: ${error.message}. Please try again or contact support if the problem persists.`
      );
      setContractValidationModalOpen(false);
    }

    setIsSubmitting(false);
  };

  useEffect(() => {
    const video = document.querySelector("video");
    if (video) {
      video.addEventListener("playing", handleVideoPlaying);
      return () => {
        video.removeEventListener("playing", handleVideoPlaying);
      };
    }
  }, []);

  useEffect(() => {
    scanResult && handleSubmit(scanResult);
  }, [scanResult]);

  useEffect(() => {
    if (step !== "qrcode") {
      return undefined;
    }
    setShowManualEntry(false);
    const timer = setTimeout(() => setShowManualEntry(true), 8000);
    return () => clearTimeout(timer);
  }, [step]);

  useEffect(() => {
    if (step !== "otp-verification") {
      otpAutoSendAttemptedRef.current = false;
      return;
    }
    const subId = subscriptionIdRef.current;
    if (!subId || otpAutoSendAttemptedRef.current || sendingOTP) {
      return;
    }
    otpAutoSendAttemptedRef.current = true;
    requestOTPForRedemption(subId);
  }, [step, sendingOTP]);

  const handleVehicleUpload = async (e) => {
    e?.preventDefault?.();

    // Detect if this is a mobile device for adjusted timeouts and compression
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    // Prevent duplicate submissions
    if (isSubmitting) {
      console.log("Submission already in progress, ignoring duplicate request");
      return;
    }

    // Check if image is still being compressed
    if (isCompressing) {
      setError("Image Compression In Progress");
      setErrorDetails("Please wait for the image to finish compressing before submitting.");
      return;
    }

    setError("");
    setErrorDetails("");
    setSuccess("");
    setUploadStatus("");

    if (!vehicleImage) {
      setUploadStatus("❌ No photo selected. Please take a photo first.");
      setError("Vehicle Photo Required");
      setErrorDetails(
        "You must take a photo of the vehicle to complete the redemption. Please take a photo and try again."
      );
      return;
    }

    // Validate file size (should be compressed, but double-check)
    const imageSizeMB = (vehicleImage.size / (1024 * 1024)).toFixed(2);
    if (vehicleImage.size > 10 * 1024 * 1024) {
      // 10MB max
      setUploadStatus(`❌ Image too large (${imageSizeMB}MB). Maximum is 10MB.`);
      setError("Image File Too Large");
      setErrorDetails(
        `The image file is too large (${imageSizeMB}MB). Maximum allowed size is 10MB. Please try taking another photo with a lower resolution.`
      );
      return;
    }

    setUploadStatus(`📤 Starting upload (${imageSizeMB}MB)...`);

    console.log("📤 Starting redemption upload:", {
      vehicleImageSize: vehicleImage.size,
      vehicleImageType: vehicleImage.type,
      hasVehicleData: !!redemptionData.vehicle,
      hasProductData: !!redemptionData.product,
      subscriptionId: redemptionData.subscriptionId
    });

    // Validate required data before proceeding
    if (!redemptionData.vehicle || !redemptionData.vehicle._id) {
      setIsSubmitting(false);
      setUploadStatus("❌ Vehicle data is missing. Please go back and try again.");
      setError("Vehicle Data Missing");
      setErrorDetails(
        "Vehicle information is required to complete the redemption. Please go back to the previous step and try again."
      );
      return;
    }

    if (!redemptionData.product || !redemptionData.product._id) {
      setIsSubmitting(false);
      setUploadStatus("❌ Product data is missing. Please go back and try again.");
      setError("Product Data Missing");
      setErrorDetails(
        "Product information is required to complete the redemption. Please go back to the previous step and try again."
      );
      return;
    }

    if (!redemptionData.subscriptionId) {
      setIsSubmitting(false);
      setUploadStatus("❌ Subscription ID is missing. Please go back and try again.");
      setError("Subscription ID Missing");
      setErrorDetails(
        "Subscription ID is required to complete the redemption. Please go back to the previous step and try again."
      );
      return;
    }

    setIsSubmitting(true);
    setUploadStatus((prev) => prev + "\n⏳ Uploading to server...");

    console.log("🚀 handleVehicleUpload: Starting upload process...");

    // Generate unique idempotency key to prevent duplicate submissions
    const idempotencyKey = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${redemptionData.vehicle._id}-${
      redemptionData.product._id
    }`;

    // Ensure vehicleImage is a File object (not just Blob) for FormData compatibility on mobile
    // Mobile browsers are stricter about FormData requiring File objects with names
    let imageFile = vehicleImage;
    if (vehicleImage instanceof Blob && !(vehicleImage instanceof File)) {
      // Convert Blob to File if needed (shouldn't happen due to our compression fix, but safety check)
      const fileExtension = vehicleImage.type?.split("/")[1] || "jpg";
      const fileName = `vehicle-photo-${Date.now()}.${fileExtension}`;
      imageFile = new File([vehicleImage], fileName, {
        type: vehicleImage.type || "image/jpeg",
        lastModified: Date.now()
      });
      console.log("⚠️ Vehicle image was Blob, converted to File:", fileName);
    }

    console.log("📋 FormData contents:", {
      vehicle: redemptionData.vehicle?._id,
      product: redemptionData.product?._id,
      subscriptionId: redemptionData.subscriptionId,
      vehicleImageSize: imageFile.size,
      vehicleImageType: imageFile.type,
      vehicleImageName: imageFile.name || "blob",
      isFile: imageFile instanceof File,
      isBlob: imageFile instanceof Blob,
      isMobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
    });

    const formData = new FormData();
    formData.append("vehicle", redemptionData.vehicle._id); // Safe now - we validated above
    formData.append("product", redemptionData.product._id); // Safe now - we validated above
    formData.append("subscriptionId", redemptionData.subscriptionId); // Pass the specific subscription ID that was scanned
    const selectedMerchantStore = sessionStorage.getItem("selectedMerchantStore");
    if (selectedMerchantStore) {
      formData.append("merchantStore", selectedMerchantStore);
    }
    formData.append("merchantUser", JSON.parse(sessionStorage.getItem("currentUser"))._id);
    formData.append("redemptionDateUTC", new Date());
    formData.append("active", true);
    formData.append("vehicleImage", imageFile, imageFile.name); // Include filename for better mobile compatibility
    formData.append("idempotencyKey", idempotencyKey);
    if (redemptionTicketRef.current) {
      formData.append("redemptionTicket", redemptionTicketRef.current);
    }
    // So backend can set createdBy/createdByName when an admin creates the redemption
    const selectedPortal = sessionStorage.getItem("selectedPortal");
    if (selectedPortal) {
      formData.append("selectedPortal", selectedPortal);
    }

    // Create AbortController for request cancellation
    const abortController = new AbortController();
    let requestTimeout = null;
    let safetyTimeout = null;

    try {
      setUploadStatus((prev) => prev + "\n📡 Sending request...");

      // Set a safety timeout to ensure we don't hang forever
      // This is in addition to axios timeout - if axios timeout fails, this will catch it
      // For mobile, use longer timeout due to slower/less reliable networks
      const safetyTimeoutDuration = isMobile ? 240000 : 180000; // 4 min for mobile, 3 min for desktop
      safetyTimeout = setTimeout(() => {
        console.error(
          `⚠️ Safety timeout triggered after ${safetyTimeoutDuration / 1000} seconds - request taking too long`
        );
        abortController.abort();
        setUploadStatus((prev) => prev + "\n⚠️ Request timeout - aborting...");
        setIsSubmitting(false);
        setError("Upload Timeout");
        setErrorDetails("The upload request timed out. Please try again or contact support if the issue persists.");
      }, safetyTimeoutDuration);

      // Progress callback to update status with upload percentage
      const handleUploadProgress = (progress) => {
        setUploadStatus((prev) => {
          // Remove any existing progress line and add new one
          const lines = prev.split("\n");
          const filteredLines = lines.filter((line) => !line.includes("📤 Upload:"));

          // Format progress message
          let progressMessage = "";
          if (progress.isIndeterminate || progress.percentCompleted === undefined) {
            // Indeterminate progress - show bytes uploaded
            progressMessage = `\n📤 Uploading... ${progress.loadedMB}MB sent`;
          } else {
            // Determinate progress - show percentage and MB
            progressMessage = `\n📤 Upload: ${progress.percentCompleted}% (${progress.loadedMB}MB${
              progress.totalMB ? ` / ${progress.totalMB}MB` : ""
            })`;
          }

          return filteredLines.join("\n") + progressMessage;
        });
      };

      // Log FormData contents for debugging
      console.log("📋 FormData being sent:", {
        vehicle: formData.get("vehicle"),
        product: formData.get("product"),
        subscriptionId: formData.get("subscriptionId"),
        hasVehicleImage: formData.has("vehicleImage"),
        vehicleImageName: formData.get("vehicleImage")?.name,
        vehicleImageSize: formData.get("vehicleImage")?.size
      });

      // Pass true for useFormData parameter - this tells api() to set Content-Type as multipart/form-data
      // Axios will automatically append the boundary parameter when it detects FormData
      // Pass progress callback to show upload percentage
      console.log("🚀 Calling API with FormData...");
      setUploadStatus((prev) => prev + "\n🚀 Request initiated...");

      // Create the API promise first - pass abort signal for cancellation
      const apiPromise = api(`v1/redemption`, "POST", formData, true, handleUploadProgress, abortController.signal);
      console.log("✅ API promise created, waiting for response...");

      // Create timeout promise - adjust based on mobile/desktop
      // For mobile, allow more time due to slower/less reliable networks
      const raceTimeout = isMobile ? 210000 : 150000; // 3.5 min for mobile, 2.5 min for desktop
      const timeoutPromise = new Promise((_, reject) => {
        requestTimeout = setTimeout(() => {
          console.error(`⏱️ Promise.race timeout triggered after ${raceTimeout / 1000} seconds`);
          setUploadStatus((prev) => prev + "\n⏱️ Timeout reached - request taking too long");
          reject(new Error("Request timeout - no response received"));
        }, raceTimeout);
      });

      // Use Promise.race to handle timeout
      console.log("📊 Starting Promise.race between API call and timeout...");
      const redemptionResponse = await Promise.race([apiPromise, timeoutPromise]);

      // Clear safety timeout if request succeeded
      if (safetyTimeout) clearTimeout(safetyTimeout);
      if (requestTimeout) clearTimeout(requestTimeout);

      // Check if response is valid
      if (!redemptionResponse) {
        throw new Error("No response received from server");
      }

      setUploadStatus((prev) => prev + "\n✅ Server responded!");

      console.log("✅ Redemption response received:", {
        success: redemptionResponse?.data?.success,
        requiresManualApproval: redemptionResponse?.data?.requiresManualApproval,
        message: redemptionResponse?.data?.message
      });

      // Check if manual approval is required
      if (redemptionResponse.data.requiresManualApproval) {
        setUploadStatus((prev) => prev + "\n⚠️ Manual approval required.");
        setManualApprovalData(redemptionResponse.data.data);
        setStep("manual-approval");
        setIsSubmitting(false);
        return;
      }

      if (redemptionResponse.data.success) {
        // Check if this was a duplicate (backend returned existing redemption)
        if (redemptionResponse.data.data?.isDuplicate) {
          setUploadStatus((prev) => prev + "\n⚠️ Redemption already exists.");
          setSuccess("Redemption Already Processed");
          setErrorDetails("This redemption was already created. Please check the redemption list.");
          setTimeout(() => {
            closeModal();
            callback();
          }, 2000);
        }
        // Check if redemption was created but image upload is pending
        else if (
          redemptionResponse.data.imageUploadPending ||
          redemptionResponse.data.errorCode === "NETWORK_ERROR_IMAGE_PENDING"
        ) {
          console.log("✅ Redemption created with pending image upload");
          setUploadStatus((prev) => prev + "\n✅ Redemption created - image upload pending");
          setSuccess("Redemption Created - Image Upload Pending");
          setErrorDetails(
            redemptionResponse.data.details ||
              "The redemption was created successfully, but the image upload failed due to a network error. You can upload the image later by editing the redemption in the redemption list."
          );
          setTimeout(() => {
            closeModal();
            callback();
          }, 3000);
        } else {
          setUploadStatus((prev) => prev + "\n✅ Redemption successful!");
          setSuccess("Redeemed Successfully!");
          setTimeout(() => {
            closeModal();
            callback();
          }, 2000);
        }
      } else {
        // Check if error response indicates redemption was created with pending image
        if (
          redemptionResponse.data.imageUploadPending ||
          redemptionResponse.data.errorCode === "NETWORK_ERROR_IMAGE_PENDING"
        ) {
          console.log("✅ Redemption created with pending image upload");
          setUploadStatus((prev) => prev + "\n✅ Redemption created - image upload pending");
          setSuccess("Redemption Created - Image Upload Pending");
          setErrorDetails(
            redemptionResponse.data.details ||
              "The redemption was created successfully, but the image upload failed due to a network error. You can upload the image later by editing the redemption in the redemption list."
          );
          setTimeout(() => {
            closeModal();
            callback();
          }, 3000);
          return;
        }

        // Display detailed error message if available
        const errorMsg = redemptionResponse.data.message || "Redemption Failed";
        setUploadStatus((prev) => `\n❌ ${errorMsg}`);
        setError(errorMsg);
        setErrorDetails(
          redemptionResponse.data.details || "An error occurred while processing the redemption. Please try again."
        );
      }
    } catch (error) {
      // Always clear loading state on error
      setIsSubmitting(false);

      // Clear any pending timeouts
      if (safetyTimeout) clearTimeout(safetyTimeout);
      if (requestTimeout) clearTimeout(requestTimeout);

      console.error("❌ Error creating redemption:", error);

      // Check for timeout specifically
      if (
        error.response?.isTimeout ||
        error.code === "ECONNABORTED" ||
        error.message?.includes("timeout") ||
        error.message?.includes("Request timeout")
      ) {
        const timeoutError = "❌ Request timed out - Server took too long to respond";
        setUploadStatus((prev) => `${prev}\n${timeoutError}`);

        // Check if redemption was created with pending status despite timeout
        // The server might have created it but response didn't arrive in time
        try {
          setUploadStatus((prev) => `${prev}\n🔍 Checking if redemption was created...`);

          // Search for recent pending redemption for this vehicle/product
          if (redemptionData.vehicle?._id && redemptionData.product?._id) {
            const searchResponse = await api("v1/redemption/search", "POST", {
              vehicle: redemptionData.vehicle._id,
              product: redemptionData.product._id,
              status: "pending",
              limit: 1,
              sortBy: "createdOnUTC",
              sortOrder: "desc"
            });

            if (searchResponse?.data?.success && searchResponse.data.data?.redemptions?.length > 0) {
              const pendingRedemption = searchResponse.data.data.redemptions[0];
              console.log("✅ Found pending redemption created despite timeout:", pendingRedemption._id);

              // Redemption was created - treat as success with pending image
              setUploadStatus((prev) => `${prev}\n✅ Redemption created - image upload pending`);
              setSuccess("Redemption Created - Image Upload Pending");
              setErrorDetails(
                "The redemption was created successfully, but the image upload failed due to a network timeout. You can upload the image later by editing the redemption in the redemption list."
              );
              setTimeout(() => {
                closeModal();
                callback();
              }, 3000);
              return;
            }
          }
        } catch (checkError) {
          console.error("Error checking for pending redemption:", checkError);
          // Continue to show timeout error if check fails
        }

        setError("Upload Timeout");
        setErrorDetails(
          `The upload request timed out. This may be due to:\n- Slow internet connection\n- Large image file\n- Production server load\n\nPlease try:\n1. Check your internet connection\n2. Take a new photo (may be smaller)\n3. Wait a moment and try again\n\nIf the issue persists, there may be a production server issue.`
        );
      }
      // Check for network errors (no response)
      else if (!error.response || error.message === "No response received from server") {
        const networkError = error.response?.isNetworkError
          ? "❌ Network error - No response from server"
          : "❌ Network error - Check internet connection";
        setUploadStatus((prev) => `${prev}\n${networkError}`);

        // Check if redemption was created with pending status despite network error
        try {
          setUploadStatus((prev) => `${prev}\n🔍 Checking if redemption was created...`);

          // Search for recent pending redemption for this vehicle/product
          if (redemptionData.vehicle?._id && redemptionData.product?._id) {
            const searchResponse = await api("v1/redemption/search", "POST", {
              vehicle: redemptionData.vehicle._id,
              product: redemptionData.product._id,
              status: "pending",
              limit: 1,
              sortBy: "createdOnUTC",
              sortOrder: "desc"
            });

            if (searchResponse?.data?.success && searchResponse.data.data?.redemptions?.length > 0) {
              const pendingRedemption = searchResponse.data.data.redemptions[0];
              console.log("✅ Found pending redemption created despite network error:", pendingRedemption._id);

              // Redemption was created - treat as success with pending image
              setUploadStatus((prev) => `${prev}\n✅ Redemption created - image upload pending`);
              setSuccess("Redemption Created - Image Upload Pending");
              setErrorDetails(
                "The redemption was created successfully, but the image upload failed due to a network error. You can upload the image later by editing the redemption in the redemption list."
              );
              setTimeout(() => {
                closeModal();
                callback();
              }, 3000);
              return;
            }
          }
        } catch (checkError) {
          console.error("Error checking for pending redemption:", checkError);
          // Continue to show network error if check fails
        }

        setError("Network Error");
        setErrorDetails(
          "Failed to connect to the server. Please check your internet connection and try again. If the image file is very large, it may be timing out - please try taking another photo."
        );
      } else if (error.response?.status === 413 || error.response?.status === 400) {
        const sizeError = `❌ File too large (Status: ${error.response.status})`;
        setUploadStatus((prev) => `${prev}\n${sizeError}`);
        // 413 = Payload Too Large, 400 might indicate file size issues
        setError("Image File Too Large");
        setErrorDetails(
          "The uploaded image is too large for the server to process. Please try taking another photo with lower resolution or wait a moment and try again."
        );
      } else if (error.response?.data?.errorCode === "REDEMPTION_WITHIN_24_HOURS") {
        setError(error.response?.data?.message || "Redemption Not Allowed");
        setErrorDetails(
          error.response?.data?.details || "This membership was already redeemed within the last 24 hours. Please wait until the cooldown period has passed before redeeming again at any participating store."
        );
      } else if (error.response?.data?.errorCode === "DUPLICATE_SAME_VISIT_CORE") {
        setError(
          error.response?.data?.message || "Core services cannot be redeemed on the same day"
        );
        setErrorDetails(
          error.response?.data?.details ||
            "These two services cannot be combined or redeemed on the same day because they are core services."
        );
      } else if (error.response?.data?.errorCode === "ONETIME_PRODUCT_ALREADY_REDEEMED") {
        setError(error.response?.data?.message || "One-Time Product Already Redeemed");
        setErrorDetails(error.response?.data?.details || "This one-time product has already been redeemed.");
      } else if (error.response?.data?.errorCode === "MERCHANT_STORE_INACTIVE") {
        setError(error.response?.data?.message || "Store Not Active");
        setErrorDetails(
          error.response?.data?.details ||
            "This store is inactive. Please select an active store from the store dropdown and try again."
        );
      } else {
        const errorMessage = error.response?.data?.message || "Redemption Failed";
        const errorDetailsMessage =
          error.response?.data?.details ||
          `An error occurred while processing the redemption. ${
            error.response?.status ? `(Status: ${error.response.status})` : ""
          } Please try again.`;
        setError(errorMessage);
        setErrorDetails(errorDetailsMessage);
      }
    } finally {
      // Always ensure loading state is cleared - critical to prevent UI freeze
      setIsSubmitting(false);

      // Clear any remaining timeouts - critical to prevent hanging
      if (safetyTimeout) {
        clearTimeout(safetyTimeout);
      }
      if (requestTimeout) {
        clearTimeout(requestTimeout);
      }
    }
  };

  const handleManualApproval = async () => {
    if (!manualApprovalData) {
      setError("Approval Data Missing");
      setErrorDetails("Unable to proceed with approval. Please try uploading the image again.");
      return;
    }

    setError("");
    setErrorDetails("");
    setSuccess("");
    setApprovingRedemption(true);

    try {
      const currentUser = JSON.parse(sessionStorage.getItem("currentUser"));

      // If redemptionId exists (legacy flow), use it. Otherwise use new data structure
      const requestData = manualApprovalData.redemptionId
        ? {
            redemptionId: manualApprovalData.redemptionId,
            userEmail: currentUser?.email || currentUser?.emailAddress || "unknown@example.com"
          }
        : {
            // New flow: no redemption exists yet, pass all data to create it
            tempImageId: manualApprovalData.tempImageId,
            imageS3Key: manualApprovalData.imageS3Key,
            imageUrl: manualApprovalData.imageUrl, // Pass the image URL as fallback
            vehicleId: manualApprovalData.vehicleId,
            productId: manualApprovalData.productId,
            subscriptionId: manualApprovalData.subscriptionId || redemptionData?.subscriptionId, // Pass the specific subscription ID that was scanned
            merchantStoreId: manualApprovalData.merchantStoreId,
            merchantUserId: manualApprovalData.merchantUserId,
            redemptionDateUTC: manualApprovalData.redemptionDateUTC,
            userEmail: currentUser?.email || currentUser?.emailAddress || "unknown@example.com",
            ...(redemptionTicketRef.current ? { redemptionTicket: redemptionTicketRef.current } : {})
          };

      const response = await api("v1/redemption/approve-manual", "POST", requestData);

      if (response?.data?.success) {
        setSuccess("Redemption Approved Successfully!");
        setTimeout(() => {
          closeModal();
          callback();
        }, 2000);
      } else {
        setError(response?.data?.message || "Approval Failed");
        setErrorDetails(
          response?.data?.details || "An error occurred while approving the redemption. Please try again."
        );
      }
    } catch (error) {
      console.error("Error approving redemption:", error);
      setError(error.response?.data?.message || "Approval Failed");
      setErrorDetails(
        error.response?.data?.details || "An error occurred while approving the redemption. Please try again."
      );
    }

    setApprovingRedemption(false);
  };

  const requestOTPForRedemption = async (subscriptionId) => {
    if (sendingOTP) {
      return;
    }

    if (subscriptionId) {
      subscriptionIdRef.current = subscriptionId;
    } else {
      setError("Subscription ID Missing");
      setErrorDetails("Subscription ID is missing. Go back and scan or enter the subscription again.");
      setOtpDeliveryStatus(OTP_DELIVERY.ERROR);
      return;
    }

    setSendingOTP(true);
    setOtpDeliveryStatus(OTP_DELIVERY.SENDING);
    setError("");
    setErrorDetails("");
    try {
      const payload = await redemptionPost("v1/redemption/request-otp", {
        subscriptionId,
      });
      setOtpSent(true);
      setOtpChannels(payload.data?.channels || null);
      setOtpDeliveryStatus(OTP_DELIVERY.SENT);
      setSuccess(
        redemptionOtpChannelsMessage(payload.data?.channels, payload.message)
      );
    } catch (error) {
      console.error("Error requesting OTP:", error);
      setOtpDeliveryStatus(OTP_DELIVERY.ERROR);
      setError("Failed to Send Code");
      setErrorDetails(
        error.message ||
          "Unable to send the verification code. Tap Resend or go back and try again."
      );
    }
    setSendingOTP(false);
  };

  const validateOTPForRedemption = async () => {
    if (!otpCode || otpCode.length !== 6) {
      setError("Invalid OTP Format");
      setErrorDetails("Please enter a valid 6-digit OTP code. The code should contain exactly 6 numbers.");
      return;
    }

    console.log("🔍 validateOTPForRedemption - Starting validation", {
      subscriptionIdRef: subscriptionIdRef.current,
      redemptionData,
      redemptionDataSubscriptionId: redemptionData?.subscriptionId,
      step: step
    });

    // Get subscriptionId from ref first (most reliable), then fallback to redemptionData
    // This avoids React async state update issues
    let subscriptionId = subscriptionIdRef.current;

    // If ref is empty but redemptionData has it, restore the ref
    if (!subscriptionId && redemptionData?.subscriptionId) {
      console.log("⚠️ Ref is empty, restoring from redemptionData:", redemptionData.subscriptionId);
      subscriptionIdRef.current = redemptionData.subscriptionId;
      subscriptionId = redemptionData.subscriptionId;
    }

    // Final fallback to redemptionData
    if (!subscriptionId) {
      subscriptionId = redemptionData?.subscriptionId;
    }

    if (!subscriptionId) {
      setError("Subscription ID Missing");
      setErrorDetails("Subscription ID is missing. Please scan the QR code again.");
      console.error("❌ OTP Validation Error: subscriptionId is missing", {
        subscriptionIdRef: subscriptionIdRef.current,
        redemptionData,
        redemptionDataKeys: redemptionData ? Object.keys(redemptionData) : "redemptionData is null/undefined",
        redemptionDataSubscriptionId: redemptionData?.subscriptionId,
        step: step
      });
      return;
    }

    console.log("✅ validateOTPForRedemption - Found subscriptionId:", subscriptionId);

    setError("");
    setErrorDetails("");
    setValidatingOTP(true);
    try {
      console.log("🔍 OTP Validation - Sending:", {
        OTP: otpCode,
        subscriptionId: subscriptionId,
        subscriptionIdRef: subscriptionIdRef.current,
        redemptionData: redemptionData,
        redemptionDataSubscriptionId: redemptionData?.subscriptionId
      });
      const response = await api("v1/redemption/validate-otp", "POST", {
        OTP: otpCode,
        subscriptionId: subscriptionId
      });

      if (response?.data?.success) {
        // Clear OTP code and messages
        setOtpCode("");
        setSuccess("");
        setError("");

        // Fetch full subscription details for confirmation
        // Use subscriptionId from ref first (most reliable), then fallback to redemptionData
        const subIdForFetch = subscriptionId || subscriptionIdRef.current || redemptionData?.subscriptionId;

        if (!subIdForFetch) {
          console.error("❌ Cannot fetch subscription details: subscriptionId is missing", {
            subscriptionId,
            subscriptionIdRef: subscriptionIdRef.current,
            redemptionData: redemptionData
          });
          setError("Subscription ID Missing");
          setErrorDetails("Subscription ID is missing. Please scan the QR code again.");
          setValidatingOTP(false);
          return;
        }

        setLoadingSubscriptionDetails(true);
        try {
          console.log("🔍 Fetching subscription details for confirmation step with subscriptionId:", subIdForFetch);
          const subscriptionResponse = await api(`v1/subscription/${subIdForFetch}`, "GET");
          if (subscriptionResponse?.data?.success) {
            const subscription = subscriptionResponse.data.data.subscription;
            // Also fetch customer details if vehicle has customer populated
            let customerDetails = null;
            if (subscription.vehicle?.customer) {
              try {
                const customerResponse = await api(`v1/customer/${subscription.vehicle.customer}`, "GET");
                if (customerResponse?.data?.success) {
                  customerDetails = customerResponse.data.data.customer;
                }
              } catch (err) {
                console.warn("Could not fetch customer details:", err);
              }
            }

            setSubscriptionDetails({
              subscriptionId: subscription._id,
              product: subscription.product,
              vehicle: subscription.vehicle,
              customer: customerDetails,
              active: subscription.active,
              validUntilDateUTC: subscription.validUntilDateUTC,
              startDateUTC: subscription.startDateUTC
            });

            // Helper function to ensure product is populated (same as in handleSubmit)
            const ensureProductIsPopulated = async (productData) => {
              if (productData && typeof productData === "object" && productData._id) {
                console.log("✅ SubscriptionDetails: Product is already populated:", productData._id);
                return productData;
              }

              if (productData && typeof productData === "string") {
                console.log("⚠️ SubscriptionDetails: Product is just an ID, fetching product details...");
                try {
                  const productResponse = await api(`v1/product/${productData}`, "GET");
                  if (productResponse?.data?.success && productResponse.data.data?.product) {
                    console.log(
                      "✅ SubscriptionDetails: Fetched product details:",
                      productResponse.data.data.product._id
                    );
                    return productResponse.data.data.product;
                  }
                } catch (productError) {
                  console.error("❌ SubscriptionDetails: Error fetching product:", productError);
                }
              }

              return productData;
            };

            // Ensure product is populated and update redemptionData
            const populatedProduct = await ensureProductIsPopulated(subscription.product);
            if (populatedProduct && populatedProduct._id) {
              console.log("✅ SubscriptionDetails: Updating redemptionData with product:", populatedProduct._id);
              setRedemptionData((prev) => ({
                ...prev,
                product: populatedProduct,
                subscriptionId: subscription._id
              }));
            } else {
              console.warn("⚠️ SubscriptionDetails: Product is missing or invalid in subscription");
            }

            // Store vehicle rego if available (check both populated object and ID string)
            if (subscription.vehicle) {
              if (typeof subscription.vehicle === "object" && subscription.vehicle.rego) {
                console.log("✅ SubscriptionDetails: Vehicle is populated, storing rego:", subscription.vehicle.rego);
                setVehicleRego(subscription.vehicle.rego);
                setRedemptionData((prev) => ({
                  ...prev,
                  vehicle: subscription.vehicle,
                  // Also ensure product is set if not already set
                  product: prev.product || populatedProduct,
                  subscriptionId: prev.subscriptionId || subscription._id
                }));
              } else if (typeof subscription.vehicle === "string") {
                console.log("⚠️ SubscriptionDetails: Vehicle is ID, fetching vehicle details...");
                // Fetch vehicle separately if it's just an ID
                try {
                  const vehicleResponse = await api(`v1/vehicle/${subscription.vehicle}`, "GET");
                  if (vehicleResponse?.data?.success && vehicleResponse.data.data?.vehicle?.rego) {
                    console.log(
                      "✅ SubscriptionDetails: Fetched vehicle, storing rego:",
                      vehicleResponse.data.data.vehicle.rego
                    );
                    setVehicleRego(vehicleResponse.data.data.vehicle.rego);
                    setRedemptionData((prev) => ({
                      ...prev,
                      vehicle: vehicleResponse.data.data.vehicle,
                      // Also ensure product is set if not already set
                      product: prev.product || populatedProduct,
                      subscriptionId: prev.subscriptionId || subscription._id
                    }));
                  }
                } catch (vehicleError) {
                  console.error("❌ SubscriptionDetails: Error fetching vehicle:", vehicleError);
                }
              }
            } else {
              // Even if vehicle is missing, ensure product and subscriptionId are set
              setRedemptionData((prev) => ({
                ...prev,
                product: prev.product || populatedProduct,
                subscriptionId: prev.subscriptionId || subscription._id
              }));
            }

            // Proceed to confirmation step
            setTimeout(() => {
              setStep("confirmation");
              setLoadingSubscriptionDetails(false);
            }, 100);
          } else {
            // If we can't fetch details, still proceed to file upload
            setLoadingSubscriptionDetails(false);
            setTimeout(() => {
              setStep("file-upload");
            }, 100);
          }
        } catch (fetchError) {
          console.error("Error fetching subscription details:", fetchError);
          // If we can't fetch details, still proceed to file upload
          setLoadingSubscriptionDetails(false);
          setTimeout(() => {
            setStep("file-upload");
          }, 100);
        }
      } else {
        setError(response?.data?.message || "OTP Validation Failed");
        setErrorDetails(
          response?.data?.details ||
            "The OTP code entered is invalid or has expired. Please ask the customer for a new code or request a new OTP."
        );
        setSuccess("");
        setLoadingSubscriptionDetails(false);
      }
    } catch (error) {
      console.error("Error validating OTP:", error);
      setError(error.response?.data?.message || "OTP Validation Failed");
      setErrorDetails(
        error.response?.data?.details ||
          "An error occurred while validating the OTP code. Please try again or request a new OTP."
      );
      setLoadingSubscriptionDetails(false);
    }
    setValidatingOTP(false);
  };

  const handleVehicleChange = (e) => {
    const { files } = e.target;
    if (!files || !files[0]) {
      setError("No file selected");
      setErrorDetails("Please select an image file.");
      return;
    }

    if (!files[0].type.startsWith("image/")) {
      setError("Invalid File Type");
      setErrorDetails("You must upload an image file. Please select a valid image.");
      e.target.value = null;
      return;
    }

    // Clear previous errors
    setError("");
    setErrorDetails("");
    setUploadStatus("");
    setIsCompressing(true);

    const fileSizeMB = (files[0].size / (1024 * 1024)).toFixed(2);
    setUploadStatus(`📸 Photo selected (${fileSizeMB}MB). Starting compression...`);

    console.log("🔍 Starting image compression:", {
      fileName: files[0].name,
      fileSize: files[0].size,
      fileType: files[0].type
    });

    // Detect if this is a mobile device for adjusted conversion threshold
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    new Compressor(files[0], {
      quality: 0.85, // Slightly higher quality (85% instead of 80%) for better clarity
      maxWidth: 1920, // Increased max width for better quality (mobile photos are often high res)
      maxHeight: 1920, // Increased max height for better quality
      convertSize: isMobile ? 2000000 : 5000000, // Convert to JPEG at lower threshold for mobile (2MB vs 5MB for desktop)
      mimeType: "image/jpeg", // Force JPEG for better compression
      success: (compressedResult) => {
        const originalFile = files[0];
        const originalSizeMB = (originalFile.size / (1024 * 1024)).toFixed(2);
        const compressedSizeMB = (compressedResult.size / (1024 * 1024)).toFixed(2);
        const compressionRatio = ((1 - compressedResult.size / originalFile.size) * 100).toFixed(1);
        const timestamp = Date.now();
        const originalFileName = originalFile.name || "vehicle-photo.jpg";

        console.log("✅ Image compression successful:", {
          originalSize: originalFile.size,
          compressedSize: compressedResult.size,
          compressionRatio: compressionRatio + "%",
          compressedType: compressedResult.type,
          isBlob: compressedResult instanceof Blob,
          hasName: !!compressedResult.name
        });

        setUploadStatus(
          `✅ Compression complete! ${originalSizeMB}MB → ${compressedSizeMB}MB (${compressionRatio}% smaller)`
        );

        // Convert Blob to File for better mobile compatibility
        // FormData needs a File object with a name, especially on mobile
        let finalImage;
        if (compressedResult instanceof Blob && !(compressedResult instanceof File)) {
          // Create a File from the Blob with a proper name
          const fileExtension = originalFileName.split(".").pop() || "jpg";
          const fileName = `vehicle-photo-${timestamp}.${fileExtension}`;
          finalImage = new File([compressedResult], fileName, {
            type: compressedResult.type || "image/jpeg",
            lastModified: Date.now()
          });
          setUploadStatus((prev) => prev + "\n📦 Converted to File format for upload");
          console.log("📦 Converted Blob to File:", {
            fileName: finalImage.name,
            fileSize: finalImage.size,
            fileType: finalImage.type
          });
        } else {
          // It's already a File object
          finalImage = compressedResult;
          setUploadStatus((prev) => prev + "\n✅ File ready for upload");
        }

        setVehicleImage(finalImage);
        setIsCompressing(false);
      },
      error: (error) => {
        console.error("❌ Image compression failed:", error);
        const errorMsg = `Failed to compress image: ${error.message || "Unknown error"}`;
        setUploadStatus(`❌ ${errorMsg}`);
        setError("Image Compression Failed");
        setErrorDetails(
          `Failed to compress the image. The image file may be too large or corrupted. Please try taking another photo. Error: ${
            error.message || "Unknown error"
          }`
        );
        setIsCompressing(false);
        e.target.value = null; // Reset file input
      }
    });
  };

  const ErrorMessage = () =>
    error && (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 mt-4 rounded">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <h4 className="text-sm font-medium text-red-800">Redemption Failed</h4>
            <div className="mt-2 text-sm text-red-700">
              <p className="font-semibold">{error}</p>
              {errorDetails && <p className="mt-2 text-red-600 whitespace-pre-line">{errorDetails}</p>}
            </div>
          </div>
        </div>
      </div>
    );

  return (
    <div>
      {isSubmitting && step === "qrcode" && <GlobalLoadingSpinner />}
      {step === "qrcode" ? (
        <div className="text-center">
          <span className="w-full text-lg mb-2">Step 1 – Scan QR Code</span>
          {isLoadingVideo && (
            <div style={{ textAlign: "center" }}>
              <span>Video is loading. Please wait.</span>
            </div>
          )}
          <video ref={ref} style={{ width: "100%", maxHeight: 280 }} />
          <div className="mt-3 flex flex-wrap items-center gap-2 justify-center">
            <TextField
              value={subscriptionIdInput}
              onChange={(e) => setSubscriptionIdInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSubmit(subscriptionIdInput);
                }
              }}
              id="redemption-subscription-id"
              label="Enter Subscription ID"
              variant="outlined"
              fullWidth
              sx={{ flex: "1 1 220px", maxWidth: 420 }}
              disabled={isSubmitting}
            />
            <Button
              size="small"
              variant="contained"
              onClick={() => handleSubmit(subscriptionIdInput)}
              disabled={isSubmitting || !subscriptionIdInput.trim()}
            >
              Submit
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-1 mb-2">
            Paste the subscription ID from the customer&apos;s app while the camera keeps scanning. Coffee Pass
            members must show their live QR code.
          </p>
          {showManualEntry && (
            <>
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-2">
                Having trouble scanning? Enter the vehicle rego below — the camera will keep trying in the
                background.
              </p>
              <Divider sx={{ my: "1rem" }}>or enter rego manually</Divider>
              <div className="flex flex-wrap items-center gap-2">
                <TextField
                  value={regoInput}
                  onChange={(e) => setRegoInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSubmit(regoInput);
                    }
                  }}
                  id="redemption-rego"
                  label="Enter vehicle rego..."
                  variant="outlined"
                  fullWidth
                  sx={{ flex: "1 1 200px" }}
                  disabled={isSubmitting}
                />
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleSubmit(regoInput)}
                  disabled={isSubmitting || !regoInput.trim()}
                >
                  Submit
                </Button>
              </div>
            </>
          )}
          <RedemptionSubscriptionPicker
            rows={
              subscriptionPickerRows ||
              (subscriptionOptions ? buildSubscriptionPickerRows(subscriptionOptions) : null)
            }
            disabled={isSubmitting}
            onSelect={(id) => {
              const sub = subscriptionOptions?.find((s) => String(s._id) === id);
              if (sub) handleSelectSubscriptionOption(sub);
            }}
          />
          <ErrorMessage />
        </div>
      ) : step === "otp-verification" ? (
        <div className="text-center">
          <span className="w-full text-lg mb-2">Step 2 – Send &amp; verify code</span>
          {loadedSubscriptionSummary && (
            <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded text-left text-sm">
              <div className="font-medium">{loadedSubscriptionSummary.productName}</div>
              <div className="text-gray-600">
                Rego {loadedSubscriptionSummary.rego}
                {loadedSubscriptionSummary.expiryLabel
                  ? ` · Expires ${loadedSubscriptionSummary.expiryLabel}`
                  : ""}
                {` · ID ${loadedSubscriptionSummary.idSuffix}`}
              </div>
            </div>
          )}
          <div
            className={`mb-4 p-2 rounded text-sm ${
              otpDeliveryStatus === OTP_DELIVERY.ERROR
                ? "bg-red-50 text-red-800 border border-red-200"
                : otpDeliveryStatus === OTP_DELIVERY.SENT
                ? "bg-green-50 text-green-800 border border-green-200"
                : "bg-blue-50 text-blue-800 border border-blue-200"
            }`}
          >
            {otpStepStatusMessage(
              otpDeliveryStatus,
              otpChannels,
              otpDeliveryStatus === OTP_DELIVERY.ERROR ? errorDetails : success
            )}
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Ask the customer for the verification code sent to their phone or email, then enter it below.
          </p>
          <div className="flex flex-col gap-4 items-center">
            <TextField
              value={otpCode}
              onChange={(e) => {
                const value = e.target.value
                  .replace(/[^0-9A-Za-z]/g, "")
                  .toUpperCase()
                  .slice(0, 6);
                setOtpCode(value);
                setError("");
              }}
              id="otp-input"
              label="Enter OTP Code"
              variant="outlined"
              placeholder="123456"
              inputProps={{
                maxLength: 6,
                style: { textAlign: "center", fontSize: "1.5rem", letterSpacing: "0.5rem" }
              }}
              fullWidth
              disabled={validatingOTP}
            />
            <div className="flex gap-2 w-full">
              <Button
                variant="outlined"
                onClick={() => {
                  setStep("qrcode");
                  setOtpCode("");
                  setOtpSent(false);
                  setError("");
                  setSuccess("");
                  setOtpDeliveryStatus(OTP_DELIVERY.IDLE);
                  setOtpChannels(null);
                  setLoadedSubscriptionSummary(null);
                  otpAutoSendAttemptedRef.current = false;
                }}
                disabled={validatingOTP}
                fullWidth
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={validateOTPForRedemption}
                disabled={validatingOTP || !otpSent || !otpCode || otpCode.length !== 6}
                fullWidth
              >
                {validatingOTP ? "Validating..." : "Verify OTP"}
              </Button>
            </div>
            <Button
              variant="text"
              onClick={() => {
                // Try ref first (most reliable), then redemptionData
                const subId = subscriptionIdRef.current || redemptionData?.subscriptionId;
                if (!subId) {
                  console.error("❌ Resend OTP Error: subscriptionId is missing", {
                    subscriptionIdRef: subscriptionIdRef.current,
                    redemptionData,
                    redemptionDataSubscriptionId: redemptionData?.subscriptionId
                  });
                  setError("Subscription ID Missing");
                  setErrorDetails("Subscription ID is missing. Please scan the QR code again.");
                  return;
                }
                otpAutoSendAttemptedRef.current = true;
                requestOTPForRedemption(subId);
              }}
              disabled={sendingOTP}
              size="small"
            >
              {sendingOTP ? "Sending..." : "Resend code"}
            </Button>
          </div>
          <ErrorMessage />
        </div>
      ) : step === "confirmation" && liveQrMode === "cafe" ? (
        <div className="text-center">
          <span className="w-full text-lg mb-4">Confirm and redeem</span>
          {success ? (
            <div className="my-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-2xl font-bold text-green-700">{success}</p>
              <p className="text-sm text-green-800 mt-2">The redemption has been recorded.</p>
            </div>
          ) : (
            <div className="text-left bg-gray-50 rounded-lg p-4 sm:p-6 my-4">
              <h4 className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Member</h4>
              <p className="text-xl font-bold text-[#10073E] mb-3">{subscriptionDetails?.memberName || "Member"}</p>
              <h4 className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Pass</h4>
              <p className="text-lg font-semibold text-gray-800 mb-3">
                {subscriptionDetails?.product?.name || "Coffee Pass"}
              </p>
              {subscriptionDetails?.redemption?.eligible ? (
                <p className="text-green-700 font-semibold">Valid. A coffee can be redeemed now.</p>
              ) : (
                <p className="text-red-700 font-semibold">
                  Not valid: {subscriptionDetails?.redemption?.reason || "this pass cannot be redeemed right now"}
                </p>
              )}
            </div>
          )}
          {!success && (
            <div className="flex gap-2 w-full">
              <Button
                variant="outlined"
                onClick={() => {
                  setStep("qrcode");
                  setSubscriptionDetails(null);
                  setError("");
                  setErrorDetails("");
                  resetLiveQr();
                }}
                disabled={cafeRedeeming}
                fullWidth
              >
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCafeRedeem}
                disabled={cafeRedeeming || !subscriptionDetails?.redemption?.eligible}
                fullWidth
              >
                {cafeRedeeming ? "Redeeming..." : "Redeem"}
              </Button>
            </div>
          )}
          <ErrorMessage />
        </div>
      ) : step === "confirmation" ? (
        <div className="text-center">
          <span className="w-full text-lg mb-4">Step 3 – Confirm Subscription Details</span>
          <p className="text-sm text-gray-600 mb-4">
            Please review the subscription details below and confirm with the customer before proceeding.
          </p>

          {loadingSubscriptionDetails ? (
            <div className="py-8">
              <GlobalLoadingSpinner />
              <p className="mt-4 text-gray-600">Loading subscription details...</p>
            </div>
          ) : subscriptionDetails ? (
            <div className="text-left bg-gray-50 rounded-lg p-4 sm:p-6 mb-4">
              {/* Service/Product Details - Most Prominent */}
              <div className="mb-4 pb-4 border-b border-gray-300">
                <h4 className="text-xs sm:text-sm font-medium text-gray-500 mb-3">Service to Provide</h4>
                <div className="bg-white px-4 sm:px-6 py-4 sm:py-5 rounded-lg border-2 border-[#10073E] shadow-md">
                  {/* Service Description - Most Visible on Mobile */}
                  {subscriptionDetails.product?.description && (
                    <p className="text-2xl sm:text-base font-bold text-[#10073E] mb-3 leading-tight">
                      {subscriptionDetails.product.description}
                    </p>
                  )}
                  {/* Product Name - Secondary on Mobile, More Prominent on Desktop */}
                  <p className="text-xs sm:text-lg font-semibold text-gray-700 mb-2">
                    {subscriptionDetails.product?.name || "Unknown Product"}
                  </p>
                  {/* Product Type - Tertiary */}
                  {subscriptionDetails.product?.type && (
                    <p className="text-xs text-gray-500 mt-2">Type: {subscriptionDetails.product.type}</p>
                  )}
                </div>
              </div>

              {/* Subscription Status */}
              <div className="mb-4">
                <h4 className="text-xs sm:text-sm font-medium text-gray-500 mb-2">Subscription Status</h4>
                <div className="bg-white px-3 sm:px-4 py-2 sm:py-3 rounded border border-gray-300">
                  <p className="text-xs sm:text-sm text-gray-700">
                    <span className="font-medium">Status:</span>{" "}
                    <span
                      className={
                        subscriptionDetails.active ? "text-green-600 font-semibold" : "text-red-600 font-semibold"
                      }
                    >
                      {subscriptionDetails.active ? "Active" : "Inactive"}
                    </span>
                  </p>
                  {subscriptionDetails.validUntilDateUTC && (
                    <p className="text-xs text-gray-500 mt-1">
                      <span className="font-medium">Valid Until:</span>{" "}
                      {new Date(subscriptionDetails.validUntilDateUTC).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-yellow-800">
                Could not load subscription details. You can still proceed with the redemption.
              </p>
            </div>
          )}

          <div className="flex gap-2 w-full">
            <Button
              variant="outlined"
              onClick={() => {
                // A live-QR wash scan never went through OTP; go back to scanning.
                if (liveQrMode === "wash-live") {
                  setStep("qrcode");
                  resetLiveQr();
                } else {
                  setStep("otp-verification");
                }
                setSubscriptionDetails(null);
                setError("");
              }}
              fullWidth
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setStep("file-upload");
              }}
              fullWidth
              color="primary"
            >
              Confirm & Continue
            </Button>
          </div>
          <ErrorMessage />
        </div>
      ) : step === "file-upload" ? (
        <div className="text-center">
          {/* Step 4 Heading - Always show */}
          <div className="mb-4">
            <h3 className="text-lg font-semibold">Step 4 – Take Photo of Vehicle</h3>
          </div>

          {(() => {
            // Check multiple sources for vehicle rego - prioritize state, then redemptionData, then subscriptionDetails
            let rego = vehicleRego;

            // Fallback to redemptionData if vehicleRego state is empty
            if (!rego && redemptionData?.vehicle) {
              if (typeof redemptionData.vehicle === "object" && redemptionData.vehicle.rego) {
                rego = redemptionData.vehicle.rego;
              }
            }

            // Final fallback to subscriptionDetails
            if (!rego && subscriptionDetails?.vehicle) {
              if (typeof subscriptionDetails.vehicle === "object" && subscriptionDetails.vehicle.rego) {
                rego = subscriptionDetails.vehicle.rego;
              }
            }

            console.log("🔍 Step 4 - Vehicle rego check:", {
              step: step,
              vehicleRegoState: vehicleRego,
              redemptionData: redemptionData,
              redemptionDataVehicle: redemptionData?.vehicle,
              redemptionDataVehicleType: typeof redemptionData?.vehicle,
              redemptionDataVehicleRego: redemptionData?.vehicle?.rego,
              subscriptionDetails: subscriptionDetails,
              subscriptionDetailsVehicle: subscriptionDetails?.vehicle,
              subscriptionDetailsVehicleType: typeof subscriptionDetails?.vehicle,
              subscriptionDetailsVehicleRego: subscriptionDetails?.vehicle?.rego,
              finalRego: rego,
              hasRego: !!rego,
              regoSource: rego
                ? vehicleRego === rego
                  ? "vehicleRego state"
                  : redemptionData?.vehicle?.rego === rego
                  ? "redemptionData.vehicle"
                  : "subscriptionDetails.vehicle"
                : "none"
            });

            // Show note if rego exists
            if (rego) {
              return (
                <div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-400 rounded">
                  <p className="text-sm text-blue-800">
                    Please take a photo of the vehicle with rego <strong>{rego}</strong>. Please make sure the rego
                    number is clearly visible.
                  </p>
                </div>
              );
            }

            // Return null if no rego (heading is already shown above)
            return null;
          })()}
          {success && success.includes("Redeemed Successfully") && (
            <span className="font-semibold text-lg" style={{ color: "green" }}>
              {success}
            </span>
          )}
          {(!success || !success.includes("Redeemed Successfully")) && (
            <div className="mt-2">
              {vehicleImage && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800 mb-2">📷 Photo taken. Click "Take Photo" to process.</p>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      setVehicleImage(null);
                      // Reset file input if it exists
                      const fileInput = document.querySelector('input[type="file"]');
                      if (fileInput) {
                        fileInput.value = "";
                      }
                    }}
                  >
                    Retake Photo
                  </Button>
                </div>
              )}
              <VehicleImageUpload
                handleChange={handleVehicleChange}
                handleSubmit={handleVehicleUpload}
                isSubmitting={isSubmitting}
                vehicleImage={vehicleImage}
                isCompressing={isCompressing}
              />
            </div>
          )}
          <ErrorMessage />
        </div>
      ) : step === "manual-approval" ? (
        <div className="text-center">
          <span className="w-full text-lg mb-4">Step 4 – Verify Registration Number</span>
          {success && <div className="mb-4 p-3 bg-green-100 text-green-800 rounded">{success}</div>}

          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-4">
              The subscription plate recognition does not match the image provided.
            </p>

            {/* Uploaded Vehicle Image */}
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Uploaded Vehicle Image</h4>
              {manualApprovalData?.imageUrl ? (
                <div className="border-2 border-gray-300 rounded-lg p-3 bg-gray-50 flex justify-center items-center">
                  <img
                    src={manualApprovalData.imageUrl}
                    alt="Uploaded vehicle"
                    className="max-w-full h-auto max-h-96 mx-auto rounded shadow-lg"
                    style={{ objectFit: "contain", maxWidth: "100%", width: "auto" }}
                  />
                </div>
              ) : (
                <div className="border-2 border-gray-300 rounded-lg p-8 bg-gray-50 text-gray-500">
                  Image not available
                </div>
              )}
            </div>

            {/* Registration Number Comparison */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h4 className="text-sm font-semibold text-blue-800 mb-3">Registration Number Comparison</h4>

              <div className="space-y-3">
                <div className="flex items-center justify-between bg-white rounded p-3">
                  <span className="text-sm font-medium text-gray-700">Subscription Registration:</span>
                  <span className="text-lg font-bold text-blue-600">
                    {manualApprovalData?.subscriptionRego || "N/A"}
                  </span>
                </div>

                {manualApprovalData?.extractedRego && (
                  <div className="flex items-center justify-between bg-white rounded p-3">
                    <span className="text-sm font-medium text-gray-700">Extracted from Image:</span>
                    <span
                      className={`text-lg font-bold ${
                        manualApprovalData.extractedRego.toUpperCase().replace(/\s/g, "") ===
                        (manualApprovalData.subscriptionRego || "").toUpperCase().replace(/\s/g, "")
                          ? "text-green-600"
                          : "text-orange-600"
                      }`}
                    >
                      {manualApprovalData.extractedRego}
                    </span>
                  </div>
                )}

                {!manualApprovalData?.extractedRego && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                    <p className="text-sm text-yellow-800">
                      ⚠️ Could not automatically read the registration number from the image. Please verify manually.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Warning message - above buttons */}
            {(!manualApprovalData?.extractedRego ||
              (manualApprovalData?.extractedRego &&
                manualApprovalData.extractedRego.toUpperCase().replace(/\s/g, "") !==
                  (manualApprovalData.subscriptionRego || "").toUpperCase().replace(/\s/g, ""))) && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-yellow-600 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-semibold text-yellow-800 mb-1">Important Warning</h4>
                    <p className="text-sm text-yellow-700">
                      By overriding and approving this redemption when the registration numbers do not match, you may
                      not receive payment for this redemption. Please ensure the vehicle registration matches the
                      subscription before approving.
                    </p>
                    {manualApprovalData?.extractedRego && (
                      <p className="text-sm text-yellow-600 mt-2">
                        💡 <strong>Tip:</strong> If the extracted registration doesn't match, you can retry with a
                        clearer image of the license plate.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button
                  variant="outlined"
                  onClick={async () => {
                    // Clear all state for retry
                    setVehicleImage(null);
                    setManualApprovalData(null);
                    setError("");
                    setErrorDetails("");
                    setSuccess("");

                    // Reset file input if it exists
                    const fileInput = document.querySelector('input[type="file"]');
                    if (fileInput) {
                      fileInput.value = "";
                    }

                    // Go back to file upload step
                    setStep("file-upload");
                  }}
                  disabled={approvingRedemption}
                  fullWidth
                >
                  Retry Upload
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleManualApproval}
                  disabled={approvingRedemption}
                  fullWidth
                >
                  {approvingRedemption ? "Approving..." : "Approve & Complete Redemption"}
                </Button>
              </div>
            </div>
            <ErrorMessage />
          </div>
        </div>
      ) : null}

      {/* Contract Validation Modal for Merchants */}
      <ModalPopup
        showModal={contractValidationModalOpen}
        handleShowModal={() => {
          setContractValidationModalOpen(false);
          setContractValidationMessage("");
        }}
        popupTitle={
          contractValidationMessage?.includes("No contract found")
            ? "Contract Not Found"
            : "Contract Signature Required"
        }
        popupContent={
          <div className="p-6">
            <div className="mb-4">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-yellow-100 rounded-full">
                <svg
                  className="w-6 h-6 text-yellow-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
                {contractValidationMessage?.includes("No contract found")
                  ? "No Contract Found for This Subscription"
                  : "Customer Contract Not Signed"}
              </h3>
              <p className="text-sm text-gray-600 text-center mb-4 whitespace-pre-line">{contractValidationMessage}</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-blue-800">Next Steps:</h4>
                  <div className="mt-2 text-sm text-blue-700">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Ask the customer to log into their Priority One customer portal</li>
                      <li>Have them sign their subscription contract</li>
                      <li>Once signed, you can process their redemption</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => {
                  setContractValidationModalOpen(false);
                  setContractValidationMessage("");
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Understood
              </button>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default RedemptionScanner;
